<template>
  <div class="home">
    <!-- Scroll Progress Bar -->
    <div class="scroll-progress" :style="{ width: scrollProgress + '%' }"></div>

    <!-- Hero Banner with Parallax -->
    <section class="hero-banner">
      <div class="hero-bg" :style="{ transform: 'translateY(' + parallaxOffset + 'px)' }">
        <div class="hero-overlay"></div>
      </div>
      <div class="hero-content">
        <div class="container">
          <div class="hero-text">
            <h1 class="hero-title" :class="{ 'animate-in': heroVisible }">
              <span class="title-line">{{ $t('home.heroTitle') }}</span>
            </h1>
            <p class="hero-subtitle" :class="{ 'animate-in': heroVisible }">
              <span class="subtitle-line">{{ $t('home.heroSubtitle') }}</span>
            </p>
            <div class="hero-buttons" :class="{ 'animate-in': heroVisible }">
              <a href="/products" class="btn btn-primary btn-animate ripple-effect">
                <i class="fas fa-box-open mr-2"></i>{{ $t('nav.products') }}
              </a>
              <a href="/contact" class="btn btn-outline-light btn-animate ripple-effect ml-3">
                <i class="fas fa-paper-plane mr-2"></i>{{ $t('nav.contact') }}
              </a>
            </div>
          </div>
        </div>
      </div>
      <div class="scroll-indicator" :class="{ 'animate-in': heroVisible }">
        <div class="mouse">
          <div class="wheel"></div>
        </div>
        <span>{{ $t('home.scrollDown') }}</span>
      </div>
    </section>

    <!-- Company Introduction -->
    <section class="company-intro py-8">
      <div class="container">
        <div class="row align-items-center">
          <div class="col-lg-6 mb-5 reveal-left">
            <div class="intro-image-wrapper">
              <img src="https://1500062809.vod-qcloud.com/6d053ea1vodcq1500062809/cefc037d5001834810594443328/JE36Y0C1bvQA.png"
                   class="img-fluid rounded shadow-lg img-hover-zoom"
                   alt="公司厂房">
              <div class="image-badge">
                <!-- <i class="fas fa-industry fa-2x mb-2"></i> -->
                <span>{{ $t('home.introBadge') }}</span>
              </div>
            </div>
          </div>
          <div class="col-lg-6 reveal-right">
            <div class="intro-content">
              <span class="section-tag">
                <i class="fas fa-star mr-2"></i>{{ $t('nav.about') }}
              </span>
              <h2 class="section-title text-left mb-4">{{ $t('home.introTitle') }}</h2>
              <p class="lead mb-4 text-muted">
                {{ $t('home.introDesc') }}
              </p>
              <p class="mb-4">
                {{ $t('home.introDetail') }}
              </p>
              <div class="intro-features">
                <div class="feature-item">
                  <i class="fas fa-check-circle"></i>
                  <span>{{ $t('home.introFeatures.equipment') }}</span>
                </div>
                <div class="feature-item">
                  <i class="fas fa-check-circle"></i>
                  <span>{{ $t('home.introFeatures.area') }}</span>
                </div>
                <div class="feature-item">
                  <i class="fas fa-check-circle"></i>
                  <span>{{ $t('home.introFeatures.warehouse') }}</span>
                </div>
              </div>
              <a href="/about" class="btn btn-primary btn-animate ripple-effect mt-4">
                <i class="fas fa-arrow-right mr-2"></i>{{ $t('home.readMore') }}
              </a>
            </div>
          </div>
        </div>

        <!-- Animated Stats -->
        <div class="stats-row row mt-8">
          <div class="col-6 col-md-3 mb-4" v-for="(stat, index) in stats" :key="index">
            <div class="stat-card reveal hover-float" :class="'stagger-' + (index + 1)">
              <div class="stat-icon">
                <i :class="stat.icon"></i>
              </div>
              <div class="stat-number">
                <span class="counter" :data-target="stat.value">{{ stat.displayValue }}</span>
                <span class="stat-unit">{{ stat.unit }}</span>
              </div>
              <div class="stat-label">{{ $t(stat.labelKey) }}</div>
            </div>
          </div>
        </div>
      </div>
    </section>

    <!-- Hot Products Section -->
    <section class="hot-products py-8 bg-light">
      <div class="container">
        <div class="section-header text-center reveal">
          <span class="section-tag">
            <i class="fas fa-fire mr-2"></i>{{ $t('home.products.title') }}
          </span>
          <h2 class="section-title">{{ $t('home.products.title') }}</h2>
          <p class="section-subtitle">{{ $t('home.products.subtitle') }}</p>
        </div>

        <div class="row">
          <div class="col-lg-3 col-md-4 col-sm-6 mb-4"
               v-for="(product, index) in hotProducts"
               :key="product.id"
               :class="'stagger-' + ((index % 4) + 1)">
            <div class="product-card reveal-scale hover-lift">
              <div class="product-image img-hover-zoom">
                <img :src="product.image" :alt="product.name">
                <div class="product-overlay">
                  <a href="/products" class="btn btn-light btn-sm">
                    <i class="fas fa-eye mr-1"></i>{{ $t('products.viewDetails') }}
                  </a>
                </div>
              </div>
              <div class="product-info">
                <h5 class="product-name">{{ product.name }}</h5>
                <div class="product-meta">
                  <span class="product-tag">
                    <i class="fas fa-tag mr-1"></i>{{ $t('home.products.hot') }}
                  </span>
                  <span class="product-views">
                    <i class="fas fa-fire mr-1"></i>{{ $t('home.products.popular') }}
                  </span>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div class="text-center mt-6 reveal">
          <a href="/products" class="btn btn-primary btn-lg btn-animate ripple-effect">
            <i class="fas fa-th-large mr-2"></i>{{ $t('home.products.viewMore') }}
            <i class="fas fa-arrow-right ml-2"></i>
          </a>
        </div>
      </div>
    </section>

    <!-- Company Advantages -->
    <section class="company-advantages py-8">
      <div class="container">
        <div class="section-header text-center reveal">
          <span class="section-tag section-tag-light">
            <i class="fas fa-trophy mr-2"></i>{{ $t('about.advantages.title') }}
          </span>
          <h2 class="section-title section-title-light">{{ $t('about.advantages.title') }}</h2>
          <p class="section-subtitle section-subtitle-light">{{ $t('about.advantages.subtitle') }}</p>
        </div>

        <div class="row">
          <div class="col-lg-3 col-md-6 mb-4"
               v-for="(advantage, index) in advantages"
               :key="index"
               :class="'stagger-' + (index + 1)">
            <div class="advantage-card reveal hover-float" :style="{ transitionDelay: (index * 0.15) + 's' }">
              <div class="advantage-icon">
                <i :class="advantage.icon"></i>
                <div class="icon-bg"></div>
              </div>
              <div class="advantage-number">{{ advantage.number }}</div>
              <h3 class="advantage-title">{{ $t(advantage.titleKey) }}</h3>
              <p class="advantage-desc">{{ $t(advantage.descKey) }}</p>
              <div class="advantage-arrow">
                <i class="fas fa-arrow-right"></i>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>

    <!-- Factory Display -->
    <section class="factory-display py-8 bg-light">
      <div class="container">
        <div class="section-header text-center reveal">
          <span class="section-tag">
            <i class="fas fa-camera mr-2"></i>{{ $t('home.factory.title') }}
          </span>
          <h2 class="section-title">{{ $t('home.factory.title') }}</h2>
          <p class="section-subtitle">{{ $t('home.factory.subtitle') }}</p>
        </div>

        <div class="gallery-grid">
          <div class="gallery-item gallery-item-1 reveal-scale" v-for="(image, index) in factoryImages.slice(0, 1)" :key="'f' + index">
            <img :src="image" class="img-fluid" :alt="'工厂图片' + (index + 1)">
            <div class="gallery-overlay">
              <i class="fas fa-search-plus fa-2x"></i>
            </div>
          </div>
          <div class="gallery-item gallery-item-2 reveal-scale stagger-1" v-for="(image, index) in factoryImages.slice(1, 3)" :key="'f' + index + 1">
            <img :src="image" class="img-fluid" :alt="'工厂图片' + (index + 2)">
            <div class="gallery-overlay">
              <i class="fas fa-search-plus fa-2x"></i>
            </div>
          </div>
          <div class="gallery-item gallery-item-3 reveal-scale stagger-2" v-for="(image, index) in factoryImages.slice(3, 6)" :key="'f' + index + 3">
            <img :src="image" class="img-fluid" :alt="'工厂图片' + (index + 4)">
            <div class="gallery-overlay">
              <i class="fas fa-search-plus fa-2x"></i>
            </div>
          </div>
          <div class="gallery-item gallery-item-4 reveal-scale stagger-3" v-for="(image, index) in factoryImages.slice(6, 8)" :key="'f' + index + 6">
            <img :src="image" class="img-fluid" :alt="'工厂图片' + (index + 7)">
            <div class="gallery-overlay">
              <i class="fas fa-search-plus fa-2x"></i>
            </div>
          </div>
        </div>
      </div>
    </section>

    <!-- News Preview -->
    <section class="news-preview py-8">
      <div class="container">
        <div class="section-header text-center reveal">
          <span class="section-tag">
            <i class="fas fa-newspaper mr-2"></i>{{ $t('home.news.title') }}
          </span>
          <h2 class="section-title">{{ $t('nav.news') }}</h2>
          <p class="section-subtitle">{{ $t('home.news.subtitle') }}</p>
        </div>

        <div class="row">
          <div class="col-lg-4 col-md-6 mb-4"
               v-for="(news, index) in newsList"
               :key="news.id"
               :class="'stagger-' + (index + 1)">
            <div class="news-card reveal hover-lift">
              <div class="news-image img-hover-zoom">
                <img :src="news.image" class="card-img-top" :alt="$t(news.titleKey)">
                <div class="news-date">
                  <span class="date-day">{{ news.day }}</span>
                  <span class="date-month">{{ $t(news.monthKey) }}</span>
                </div>
              </div>
              <div class="news-body">
                <span class="news-category">
                  <i class="fas fa-bookmark mr-1"></i>{{ $t('news.categories.industry') }}
                </span>
                <h5 class="news-title">{{ $t(news.titleKey) }}</h5>
                <p class="news-excerpt">{{ $t(news.excerptKey) }}</p>
                <div class="news-footer">
                  <span class="news-date-text">
                    <i class="fas fa-calendar-alt mr-2"></i>{{ news.date }}
                  </span>
                  <a href="/news" class="news-link">
                    {{ $t('news.readMore') }} <i class="fas fa-arrow-right ml-1"></i>
                  </a>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div class="text-center mt-6 reveal">
          <a href="/news" class="btn btn-outline-primary btn-lg">
            <i class="fas fa-list-ul mr-2"></i>{{ $t('home.news.viewAll') }}
            <i class="fas fa-arrow-right ml-2"></i>
          </a>
        </div>
      </div>
    </section>

    <!-- CTA Section -->
    <section class="cta-section py-8">
      <div class="cta-bg"></div>
      <div class="container">
        <div class="cta-content reveal">
          <h2 class="cta-title">{{ $t('home.cta.title') }}</h2>
          <p class="cta-text">{{ $t('home.cta.text') }}</p>
          <div class="cta-buttons">
            <a href="/contact" class="btn btn-light btn-lg btn-animate ripple-effect">
              <i class="fas fa-phone mr-2"></i>{{ $t('home.cta.contact') }}
            </a>
<!--            <a href="https://wa.me/8615163903660" target="_blank" class="btn btn-outline-light btn-lg btn-animate ripple-effect ml-3">-->
<!--              <i class="fab fa-whatsapp mr-2"></i>WhatsApp-->
<!--            </a>-->
          </div>
        </div>
      </div>
    </section>
  </div>
</template>

<script>
export default {
  name: 'Home',
  data() {
    return {
      scrollProgress: 0,
      parallaxOffset: 0,
      heroVisible: false,
      statsAnimated: false,
      stats: [
        {
          icon: 'fas fa-calendar-alt',
          value: 2002,
          displayValue: 2002,
          unit: '',
          labelKey: 'home.stats.founded'
        },
        {
          icon: 'fas fa-users',
          value: 215,
          displayValue: 0,
          unit: '+',
          labelKey: 'home.stats.employees'
        },
        {
          icon: 'fas fa-expand-arrows-alt',
          value: 20000,
          displayValue: 0,
          unit: 'm²',
          labelKey: 'home.stats.area'
        },
        {
          icon: 'fas fa-box',
          value: 5000,
          displayValue: 0,
          unit: '+',
          labelKey: 'home.stats.exports'
        }
      ],
      advantages: [
        {
          icon: 'fas fa-industry',
          number: '01',
          titleKey: 'home.advantages.direct.title',
          descKey: 'home.advantages.direct.desc'
        },
        {
          icon: 'fas fa-palette',
          number: '02',
          titleKey: 'home.advantages.custom.title',
          descKey: 'home.advantages.custom.desc'
        },
        {
          icon: 'fas fa-award',
          number: '03',
          titleKey: 'home.advantages.quality.title',
          descKey: 'home.advantages.quality.desc'
        },
        {
          icon: 'fas fa-truck',
          number: '04',
          titleKey: 'home.advantages.delivery.title',
          descKey: 'home.advantages.delivery.desc'
        }
      ],
      hotProducts: [
        { id: 1, name: '', image: 'https://1500062809.vod-qcloud.com/6d053ea1vodcq1500062809/0e68bb255001834810618159785/Af9HA4KETFIA.jpg' },
        { id: 2, name: '', image: 'https://1500062809.vod-qcloud.com/6d053ea1vodcq1500062809/aa936d185001834810603550828/Lv41h0R5aS0A.jpg' },
        { id: 3, name: '', image: 'https://1500062809.vod-qcloud.com/6d053ea1vodcq1500062809/b3a84c055001834810597354881/8NOnc1iTQ5UA.jpg' },
        { id: 4, name: '', image: 'https://1500062809.vod-qcloud.com/6d053ea1vodcq1500062809/b92b4c625001834810610944995/menOyPifDyYA.jpg' },
        { id: 5, name: '', image: 'https://1500062809.vod-qcloud.com/6d053ea1vodcq1500062809/9ec76f1a5001834810620693085/MRpgshanPesA.jpg' },
        { id: 6, name: '', image: 'https://1500062809.vod-qcloud.com/6d053ea1vodcq1500062809/075bbc405001834810598618332/HBSKrjfUdMcA.jpg' },
        { id: 7, name: '', image: 'https://1500062809.vod-qcloud.com/6d053ea1vodcq1500062809/3d213b1e5001834810622859641/1Crs2e6kcLAA.jpg' },
        { id: 8, name: '', image: 'https://1500062809.vod-qcloud.com/6d053ea1vodcq1500062809/6c948e905001834810609027500/ysTyjQWv5v4A.jpg' }
      ],
      factoryImages: [
        'https://1500062809.vod-qcloud.com/6d053ea1vodcq1500062809/175e7b175001834810618501627/IIH76CDaflYA.jpg',
        'https://1500062809.vod-qcloud.com/6d053ea1vodcq1500062809/cd20b6f45001834810600976131/qAtJCfSK4xkA.jpg',
        'https://1500062809.vod-qcloud.com/6d053ea1vodcq1500062809/0e75c58f5001834810618160015/fIP8qnIi5coA.jpg',
        'https://1500062809.vod-qcloud.com/6d053ea1vodcq1500062809/0e75c6595001834810618160079/jgCakz09dTwA.jpg',
        'https://1500062809.vod-qcloud.com/6d053ea1vodcq1500062809/cd20b7805001834810600976179/wc6qaavvTLYA.jpg',
        'https://1500062809.vod-qcloud.com/6d053ea1vodcq1500062809/491576be5001834810599172343/VsAivopRDvEA.jpg',
        'https://1500062809.vod-qcloud.com/6d053ea1vodcq1500062809/2feeeebd5001834810608728272/07iYGVAbm24A.jpg',
        'https://1500062809.vod-qcloud.com/6d053ea1vodcq1500062809/e56d90065001834810607847658/0VTv2XLFk6QA.jpg'
      ],
      newsList: [
        {
          id: 1,
          titleKey: 'home.news.items.1.title',
          excerptKey: 'home.news.items.1.excerpt',
          date: '2022-09-19',
          day: '19',
          monthKey: 'home.news.items.1.month',
          image: 'https://1500062809.vod-qcloud.com/6d053ea1vodcq1500062809/b3c686f15001834810603935301/guklx3lUbr0A.jpg'
        },
        {
          id: 2,
          titleKey: 'home.news.items.2.title',
          excerptKey: 'home.news.items.2.excerpt',
          date: '2022-09-19',
          day: '19',
          monthKey: 'home.news.items.2.month',
          image: 'https://1500062809.vod-qcloud.com/6d053ea1vodcq1500062809/6ca2bc125001834810609030076/QDELp4fQ0DcA.jpg'
        },
        {
          id: 3,
          titleKey: 'home.news.items.3.title',
          excerptKey: 'home.news.items.3.excerpt',
          date: '2022-09-19',
          day: '19',
          monthKey: 'home.news.items.3.month',
          image: 'https://1500062809.vod-qcloud.com/6d053ea1vodcq1500062809/f824da105001834810611341427/cHROcQncNH8A.jpg'
        }
      ]
    }
  },
  mounted() {
    this.initAnimations()
    this.initParallax()
    this.initScrollProgress()
    this.initHeroAnimation()
  },
  destroyed() {
    window.removeEventListener('scroll', this.handleScroll)
  },
  methods: {
    initHeroAnimation() {
      setTimeout(() => {
        this.heroVisible = true
      }, 300)
    },
    initScrollProgress() {
      window.addEventListener('scroll', () => {
        const winScroll = document.body.scrollTop || document.documentElement.scrollTop
        const height = document.documentElement.scrollHeight - document.documentElement.clientHeight
        this.scrollProgress = (winScroll / height) * 100
      })
    },
    initParallax() {
      window.addEventListener('scroll', () => {
        this.parallaxOffset = window.scrollY * 0.5
      })
    },
    initAnimations() {
      const reveals = document.querySelectorAll('.reveal, .reveal-left, .reveal-right, .reveal-scale, .reveal-rotate')

      const revealOnScroll = () => {
        reveals.forEach(element => {
          const windowHeight = window.innerHeight
          const elementTop = element.getBoundingClientRect().top
          const revealPoint = 150

          if (elementTop < windowHeight - revealPoint) {
            element.classList.add('active')
          }
        })

        // Animate counters when stats section is visible
        const statsSection = document.querySelector('.stats-row')
        if (statsSection && !this.statsAnimated) {
          const statsTop = statsSection.getBoundingClientRect().top
          if (statsTop < window.innerHeight - 100) {
            this.statsAnimated = true
            this.animateCounters()
          }
        }
      }

      window.addEventListener('scroll', revealOnScroll)
      window.addEventListener('load', revealOnScroll)

      // Initial check
      revealOnScroll()
    },
    animateCounters() {
      this.stats.forEach((stat, index) => {
        if (stat.value === 2002) {
          // Don't animate the year
          stat.displayValue = stat.value
          return
        }

        const duration = 2000
        const startTime = performance.now()
        const target = stat.value

        const animate = (currentTime) => {
          const elapsed = currentTime - startTime
          const progress = Math.min(elapsed / duration, 1)

          // Easing function
          const easeOutQuart = 1 - Math.pow(1 - progress, 4)
          stat.displayValue = Math.floor(easeOutQuart * target)

          if (progress < 1) {
            requestAnimationFrame(animate)
          }
        }

        setTimeout(() => {
          requestAnimationFrame(animate)
        }, index * 200)
      })
    }
  }
}
</script>

<style scoped>
/* Hero Section */
.hero-banner {
  position: relative;
  height: 100vh;
  min-height: 700px;
  overflow: hidden;
  display: flex;
  align-items: center;
}

.hero-bg {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 120%;
  background: linear-gradient(135deg, rgba(0,0,0,0.7) 0%, rgba(0,0,0,0.4) 100%),
              url('https://1500062809.vod-qcloud.com/6d053ea1vodcq1500062809/0c831c475001834810598885522/4h2Q3vcE4bwA.jpg');
  background-size: cover;
  background-position: center;
  will-change: transform;
}

.hero-overlay {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: radial-gradient(ellipse at center, transparent 0%, rgba(0,0,0,0.3) 100%);
}

.hero-content {
  position: relative;
  z-index: 10;
  width: 100%;
  padding: 0 20px;
}

.hero-text {
  max-width: 800px;
}

.hero-title {
  font-size: 4rem;
  font-weight: 800;
  color: white;
  margin-bottom: 20px;
  line-height: 1.2;
}

.title-line {
  display: block;
  opacity: 0;
  transform: translateY(40px);
  transition: all 0.8s cubic-bezier(0.4, 0, 0.2, 1);
}

.hero-title.animate-in .title-line {
  opacity: 1;
  transform: translateY(0);
  transition-delay: 0.2s;
}

.hero-subtitle {
  font-size: 1.5rem;
  color: rgba(255,255,255,0.9);
  margin-bottom: 40px;
}

.subtitle-line {
  display: inline-block;
  opacity: 0;
  transform: translateY(30px);
  transition: all 0.8s cubic-bezier(0.4, 0, 0.2, 1);
}

.hero-subtitle.animate-in .subtitle-line {
  opacity: 1;
  transform: translateY(0);
  transition-delay: 0.5s;
}

.hero-buttons {
  opacity: 0;
  transform: translateY(30px);
  transition: all 0.8s cubic-bezier(0.4, 0, 0.2, 1);
}

.hero-buttons.animate-in {
  opacity: 1;
  transform: translateY(0);
  transition-delay: 0.8s;
}

.hero-buttons .btn {
  padding: 16px 40px;
  font-size: 1.1rem;
  border-radius: 50px;
}

.btn-outline-light {
  border: 2px solid rgba(255,255,255,0.8);
  color: white;
  background: transparent;
}

.btn-outline-light:hover {
  background: white;
  color: #333;
  border-color: white;
}

/* Scroll Indicator */
.scroll-indicator {
  position: absolute;
  bottom: 40px;
  left: 50%;
  transform: translateX(-50%);
  text-align: center;
  color: white;
  opacity: 0;
  transition: opacity 0.8s ease;
}

.scroll-indicator.animate-in {
  opacity: 1;
  transition-delay: 1.2s;
}

.mouse {
  width: 26px;
  height: 40px;
  border: 2px solid white;
  border-radius: 13px;
  margin: 0 auto 10px;
  position: relative;
}

.wheel {
  width: 4px;
  height: 8px;
  background: white;
  border-radius: 2px;
  position: absolute;
  top: 8px;
  left: 50%;
  transform: translateX(-50%);
  animation: scrollWheel 2s infinite;
}

@keyframes scrollWheel {
  0% {
    opacity: 1;
    transform: translateX(-50%) translateY(0);
  }
  100% {
    opacity: 0;
    transform: translateX(-50%) translateY(15px);
  }
}

.scroll-indicator span {
  font-size: 0.85rem;
  opacity: 0.8;
}

/* Intro Section */
.intro-image-wrapper {
  position: relative;
}

.image-badge {
  position: absolute;
  bottom: -20px;
  right: -20px;
  background: linear-gradient(135deg, #e53935, #c62828);
  color: white;
  padding: 20px 30px;
  border-radius: 10px;
  text-align: center;
  box-shadow: 0 10px 30px rgba(229, 57, 53, 0.4);
  animation: float 3s ease-in-out infinite;
}

.intro-features {
  display: flex;
  flex-wrap: wrap;
  gap: 15px;
  margin-top: 20px;
}

.feature-item {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 8px 16px;
  background: #fff5f5;
  border-radius: 25px;
  font-size: 0.95rem;
  color: #666;
}

.feature-item i {
  color: #e53935;
}

/* Stats Cards */
.stat-card {
  background: white;
  padding: 30px 20px;
  border-radius: 16px;
  text-align: center;
  box-shadow: 0 5px 20px rgba(0,0,0,0.05);
  border: 1px solid #f0f0f0;
}

.stat-icon {
  width: 60px;
  height: 60px;
  margin: 0 auto 15px;
  background: linear-gradient(135deg, #fff5f5, #ffebee);
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #e53935;
  font-size: 1.5rem;
}

.stat-number {
  font-size: 2.5rem;
  font-weight: 800;
  color: #e53935;
  line-height: 1;
}

.stat-unit {
  font-size: 1.5rem;
}

.stat-label {
  font-size: 1rem;
  color: #7f8c8d;
  margin-top: 10px;
}

/* Section Headers */
.section-tag {
  display: inline-block;
  padding: 8px 20px;
  background: linear-gradient(135deg, #fff5f5, #ffebee);
  color: #e53935;
  border-radius: 25px;
  font-size: 0.9rem;
  font-weight: 500;
  margin-bottom: 15px;
}

.section-tag-light {
  background: linear-gradient(135deg, rgba(255,255,255,0.1), rgba(255,255,255,0.05));
  color: white;
}

.section-title {
  font-size: 2.5rem;
  font-weight: 700;
  color: #333;
  margin-bottom: 15px;
}

.section-title-light {
  color: white;
}

.section-subtitle {
  font-size: 1.1rem;
  color: #7f8c8d;
  margin-bottom: 50px;
}

.section-subtitle-light {
  color: rgba(255,255,255,0.8);
}

/* Product Cards */
.product-card {
  background: white;
  border-radius: 16px;
  overflow: hidden;
  box-shadow: 0 5px 20px rgba(0,0,0,0.05);
  border: 1px solid #f0f0f0;
}

.product-image {
  position: relative;
  height: 220px;
  overflow: hidden;
}

.product-image img {
  width: 100%;
  height: 100%;
  object-fit: cover;
  transition: transform 0.5s ease;
}

.product-overlay {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: rgba(0,0,0,0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  opacity: 0;
  transition: opacity 0.3s ease;
}

.product-image:hover .product-overlay {
  opacity: 1;
}

.product-info {
  padding: 20px;
}

.product-name {
  font-size: 1.1rem;
  font-weight: 600;
  color: #333;
  margin-bottom: 10px;
}

.product-meta {
  display: flex;
  gap: 10px;
}

.product-tag, .product-views {
  font-size: 0.8rem;
  padding: 4px 12px;
  border-radius: 15px;
  display: inline-flex;
  align-items: center;
}

.product-tag {
  background: #fff5f5;
  color: #e53935;
}

.product-views {
  background: #fff8e1;
  color: #f57c00;
}

/* Advantage Cards */
.advantage-card {
  background: white;
  padding: 35px 25px;
  border-radius: 20px;
  text-align: center;
  box-shadow: 0 10px 40px rgba(0,0,0,0.05);
  border: 1px solid #f0f0f0;
  position: relative;
  overflow: hidden;
  height: 100%;
  transition: all 0.4s cubic-bezier(0.4, 0, 0.2, 1);
}

.advantage-card:hover {
  transform: translateY(-10px);
  box-shadow: 0 20px 50px rgba(229, 57, 53, 0.15);
  border-color: #e53935;
}

.advantage-icon {
  width: 80px;
  height: 80px;
  margin: 0 auto 20px;
  position: relative;
}

.advantage-icon i {
  font-size: 2.5rem;
  color: #e53935;
  position: relative;
  z-index: 2;
  transition: transform 0.3s ease;
}

.advantage-card:hover .advantage-icon i {
  transform: scale(1.1);
}

.icon-bg {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: linear-gradient(135deg, #fff5f5, #ffebee);
  border-radius: 50%;
  z-index: 1;
  transition: all 0.3s ease;
}

.advantage-card:hover .icon-bg {
  background: linear-gradient(135deg, #e53935, #c62828);
}

.advantage-card:hover .icon-bg i {
  color: white;
}

.advantage-number {
  position: absolute;
  top: 15px;
  right: 20px;
  font-size: 3.5rem;
  font-weight: 800;
  color: #e53935;
  opacity: 0.1;
  transition: opacity 0.3s ease;
}

.advantage-card:hover .advantage-number {
  opacity: 0.2;
}

.advantage-title {
  font-size: 1.3rem;
  font-weight: 600;
  color: #333;
  margin-bottom: 15px;
}

.advantage-desc {
  font-size: 0.95rem;
  color: #666;
  line-height: 1.7;
  margin-bottom: 20px;
}

.advantage-arrow {
  width: 40px;
  height: 40px;
  margin: 0 auto;
  background: #f8f9fa;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #e53935;
  transition: all 0.3s ease;
}

.advantage-arrow i {
  transition: transform 0.3s ease;
}

.advantage-card:hover .advantage-arrow {
  background: #e53935;
  color: white;
}

.advantage-card:hover .advantage-arrow i {
  transform: translateX(3px);
}

/* Gallery Grid */
.gallery-grid {
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  grid-template-rows: repeat(3, 200px);
  gap: 20px;
}

.gallery-item {
  position: relative;
  overflow: hidden;
  border-radius: 16px;
  cursor: pointer;
}

.gallery-item img {
  width: 100%;
  height: 100%;
  object-fit: cover;
  transition: transform 0.5s ease;
}

.gallery-item:hover img {
  transform: scale(1.1);
}

.gallery-item-1 {
  grid-column: 1 / 2;
  grid-row: 1 / 3;
}

.gallery-item-2 {
  grid-column: 2 / 3;
  grid-row: 1 / 2;
}

.gallery-item-3 {
  grid-column: 3 / 4;
  grid-row: 1 / 2;
}

.gallery-item-4 {
  grid-column: 4 / 5;
  grid-row: 1 / 2;
}

.gallery-item:nth-child(n+5) {
  grid-column: span 1;
  grid-row: span 1;
}

.gallery-overlay {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: rgba(229, 57, 53, 0.7);
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  opacity: 0;
  transition: opacity 0.3s ease;
}

.gallery-item:hover .gallery-overlay {
  opacity: 1;
}

/* News Cards */
.news-card {
  background: white;
  border-radius: 16px;
  overflow: hidden;
  box-shadow: 0 5px 20px rgba(0,0,0,0.05);
  border: 1px solid #f0f0f0;
  height: 100%;
}

.news-image {
  position: relative;
  height: 200px;
  overflow: hidden;
}

.news-image img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.news-date {
  position: absolute;
  top: 15px;
  right: 15px;
  background: white;
  padding: 10px 15px;
  border-radius: 10px;
  text-align: center;
  box-shadow: 0 5px 15px rgba(0,0,0,0.1);
}

.date-day {
  display: block;
  font-size: 1.5rem;
  font-weight: 700;
  color: #e53935;
  line-height: 1;
}

.date-month {
  font-size: 0.8rem;
  color: #666;
}

.news-body {
  padding: 25px;
}

.news-category {
  display: inline-block;
  padding: 5px 12px;
  background: #fff5f5;
  color: #e53935;
  border-radius: 20px;
  font-size: 0.8rem;
  margin-bottom: 15px;
}

.news-title {
  font-size: 1.15rem;
  font-weight: 600;
  color: #333;
  margin-bottom: 10px;
  line-height: 1.5;
}

.news-excerpt {
  font-size: 0.9rem;
  color: #666;
  line-height: 1.6;
  margin-bottom: 15px;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
}

.news-footer {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding-top: 15px;
  border-top: 1px solid #f0f0f0;
}

.news-date-text {
  font-size: 0.85rem;
  color: #999;
}

.news-link {
  font-size: 0.85rem;
  color: #e53935;
  font-weight: 500;
  transition: color 0.3s ease;
}

.news-link:hover {
  color: #c62828;
}

/* CTA Section */
.cta-section {
  position: relative;
  overflow: hidden;
}

.cta-bg {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: linear-gradient(135deg, #e53935 0%, #c62828 50%, #b71c1c 100%);
  background-size: 200% 200%;
  animation: gradientShift 5s ease infinite;
}

.cta-content {
  position: relative;
  z-index: 2;
  text-align: center;
  color: white;
}

.cta-title {
  font-size: 2.5rem;
  font-weight: 700;
  margin-bottom: 20px;
}

.cta-text {
  font-size: 1.2rem;
  opacity: 0.9;
  margin-bottom: 40px;
  max-width: 600px;
  margin-left: auto;
  margin-right: auto;
}

.cta-buttons .btn {
  padding: 16px 40px;
  font-size: 1.1rem;
  border-radius: 50px;
}

.cta-buttons .btn-outline-light {
  border: 2px solid white;
}

.cta-buttons .btn-outline-light:hover {
  background: white;
  color: #e53935;
}

/* Utilities */
.py-8 {
  padding-top: 100px;
  padding-bottom: 100px;
}

.mt-8 {
  margin-top: 80px;
}

.text-left {
  text-align: left;
}

/* Responsive */
@media (max-width: 991px) {
  .hero-title {
    font-size: 2.8rem;
  }

  .hero-subtitle {
    font-size: 1.2rem;
  }

  .gallery-grid {
    grid-template-columns: repeat(2, 1fr);
    grid-template-rows: repeat(4, 180px);
  }

  .gallery-item-1 {
    grid-column: 1 / 2;
    grid-row: 1 / 2;
  }

  .gallery-item-2 {
    grid-column: 2 / 3;
    grid-row: 1 / 2;
  }

  .gallery-item-3 {
    grid-column: 1 / 2;
    grid-row: 2 / 3;
  }

  .gallery-item-4 {
    grid-column: 2 / 3;
    grid-row: 2 / 3;
  }

  .image-badge {
    position: static;
    margin-top: 20px;
    display: inline-block;
  }

  .cta-title {
    font-size: 2rem;
  }
}

@media (max-width: 767px) {
  .hero-banner {
    min-height: 600px;
  }

  .hero-title {
    font-size: 2.2rem;
  }

  .hero-buttons {
    flex-direction: column;
    gap: 15px;
  }

  .hero-buttons .btn {
    width: 100%;
    margin-left: 0 !important;
  }

  .gallery-grid {
    grid-template-columns: 1fr;
    grid-template-rows: auto;
  }

  .gallery-item {
    height: 200px;
  }

  .gallery-item-1,
  .gallery-item-2,
  .gallery-item-3,
  .gallery-item-4 {
    grid-column: 1 / 2;
    grid-row: auto;
  }

  .stat-number {
    font-size: 2rem;
  }

  .cta-title {
    font-size: 1.6rem;
  }

  .cta-buttons {
    flex-direction: column;
  }

  .cta-buttons .btn {
    width: 100%;
    margin-left: 0 !important;
    margin-bottom: 15px;
  }
}
</style>
